import React, { useState, useRef, useEffect, useContext } from 'react';
import { ArrowDown2 } from 'iconsax-react';
import { ThemeContext } from '../theme';

/**
 * Dropdown field matching the list-style Dropdown used in PersonalisedStoryScreen.
 * Opens a vertical list of options below the trigger; selected row is highlighted.
 *
 * Props:
 *   label    — field label text
 *   options  — string[]
 *   value    — currently selected option
 *   onChange — (value: string) => void
 *   scale    — uniform size multiplier (default 1)
 */
export default function SelectField({ label, required = false, options, value, onChange, placeholder = '', scale = 1, dropUp = false }) {
  const theme = useContext(ThemeContext);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const s = (n) => Math.round(n * scale);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: s(12), width: '100%', position: 'relative' }}>

      {/* Label */}
      <div
        className="font-satoshi"
        style={{
          fontWeight: 700,
          fontSize: s(18),
          lineHeight: `${s(24)}px`,
          color: theme.text100,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
        {required && <span style={{ color: '#d46c6c' }}>*</span>}
      </div>

      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className="font-satoshi"
        style={{
          width: '100%',
          height: s(64),
          padding: `0 ${s(24)}px`,
          border: open ? `1.5px solid ${theme.settingsActive}` : `1.5px solid ${theme.inputBorder}`,
          borderRadius: s(20),
          backgroundColor: theme.inputBg,
          color: theme.text100,
          fontSize: s(18),
          cursor: 'pointer',
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          textAlign: 'left',
          transition: 'border-color 0.15s',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? theme.text100 : theme.placeholderColor }}>
          {value || placeholder}
        </span>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}>
          <ArrowDown2 size={s(20)} variant="Linear" color={theme.text400} />
        </span>
      </button>

      {/* Options list */}
      {open && (
        <div style={{
          position: 'absolute',
          ...(dropUp ? { bottom: '100%', marginBottom: s(4) } : { top: '100%', marginTop: s(4) }),
          left: 0,
          right: 0,
          zIndex: 200,
          backgroundColor: theme.cardBg,
          border: `1.5px solid ${theme.border}`,
          borderRadius: s(16),
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {options.map(opt => {
            const selected = opt === value;
            return (
              <div
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                className="font-satoshi"
                style={{
                  padding: `${s(8)}px ${s(16)}px`,
                  fontSize: s(18),
                  lineHeight: `${s(28)}px`,
                  cursor: 'pointer',
                  color: selected ? theme.btnPrimaryBg : theme.text100,
                  backgroundColor: selected ? theme.bg100 : theme.cardBg,
                  fontWeight: selected ? 600 : 400,
                }}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
